<template>
	<chart v-if="dataIsLoaded" :chartOptions="chartOptions" :itemsPerPage="itemsPerPage" :dynamicXAxis="dynamicXAxis"></chart>
</template>

<script>
import ClinicHistoryRepository from '@/repositories/ClinicHistoryRepository'
import Chart from '@/components/base/Chart.vue'
import { BLUE_COLOUR, RED_COLOUR, GRAY_COLOUR, BACKGROUND_COLOUR } from '@/constants/colours'
import { formatDate, formatDateForGraphAxis, getDDMMYYYY } from '@/helpers/date.helpers'
import { BLOOD_PRESSURE } from '@/constants/clinicHistory'
import clinicHistoryVariableColorsMixin from '@/mixins/clinicHistoryVariableColors.mixin'
import i18nMixin from '@/mixins/i18n.mixin'

const I18N_FILE = 'clinic-history'
const CHART_MAX_POINTS = 14
const ITEMS_PER_PAFE_EXCEEDING_MAX_POINTS = 10

export default {
	name: 'clinic-history-variable-chart',
	mixins: [clinicHistoryVariableColorsMixin, i18nMixin(I18N_FILE)],
	components: {
		Chart
	},
	data () {
		return {
			chartOptions: []
		}
	},
	mounted () {
		ClinicHistoryRepository().getHistoryChart(this.variableName).then(response => {
			let data = response.data.data
			var chartOptions = this.chartBaseOptions
			chartOptions.title.text = this.label
			chartOptions.series[0].data = data.map(item => {
				return {
					x: new Date(item.date),
					y: this.minMaxTypeChart ? item.value.max : item.value,
					color: this.coloringPoints ? this.getValueColor(item.value) : BLUE_COLOUR
				}
			})
			chartOptions.series[0].name = this.minMaxTypeChart ? this.$t('maximum') : this.label
			chartOptions.series[1].data = this.minMaxTypeChart ? data.map(item => { return { x: new Date(item.date), y: item.value.min } }) : null
			chartOptions.series[1].name = this.minMaxTypeChart ? this.$t('minimum') : null
			chartOptions.series[1].visible = this.minMaxTypeChart
			chartOptions.legend.enabled = this.minMaxTypeChart

			if (this.oneValuePerDay) {
				// Remove values with the same day date
				chartOptions.series[0].data = chartOptions.series[0].data.filter((item, index, self) => self.findIndex(t => getDDMMYYYY(t.x) === getDDMMYYYY(item.x)) === index)
				// Remove also repeated tickPositions:
				/* chartOptions.xAxis.tickPositions = chartOptions.xAxis.tickPositions.filter((item, index, self) => self.findIndex(t => getDDMMYYYY(new Date(t)) === getDDMMYYYY(new Date(item))) === index) */
			}

			this.chartOptions = chartOptions
		})
	},
	props: {
		variableName: String,
		coloringPoints: {
			type: Boolean,
			required: false,
			default: false
		},
		dynamicXAxis: {
			type: Boolean,
			required: false,
			default: false
		},
		oneValuePerDay: {
			type: Boolean,
			required: false,
			default: false
		},
		label: {
			type: String,
			required: false,
			default () {
				return this.$t(`${this.variableName}`)
			}
		},
		maxValue: {
			type: Number,
			required: false
		}
	},
	computed: {
		itemsPerPage () {
			// Divide the data in pages of the same length if there are more than CHART_MAX_POINTS
			const dataLength = this.chartOptions.series[0].data.length
			if (dataLength > CHART_MAX_POINTS) {
				const rest = dataLength % ITEMS_PER_PAFE_EXCEEDING_MAX_POINTS
				return rest < 5 ? ITEMS_PER_PAFE_EXCEEDING_MAX_POINTS + rest : ITEMS_PER_PAFE_EXCEEDING_MAX_POINTS
			} else {
				return dataLength
			}
		},
		measureUnit () {
			const unitMsgKey = `measureUnits.${this.variableName}`
			return !this.$t(unitMsgKey).includes('measureUnits.') ? ` ${this.$t(unitMsgKey)}` : ''
		},
		dataIsLoaded () {
			return this.chartOptions && this.chartOptions.series && this.chartOptions.series[0]
		},
		minMaxTypeChart () {
			return this.variableName === BLOOD_PRESSURE
		},
		chartBaseOptions () {
			const _this = this
			return {
					title: {
						text: '',
						style: {
							color: BLUE_COLOUR
						}
					},
					plotOptions: {
						series: {
							lineWidth: 2,
							events: {
								legendItemClick: function () {
									return false
								}
							}
						}
					},
					legend: {
						enabled: false
					},
					xAxis: {
						type: 'datetime',
						title: {
							text: this.$t('day')
						},
						tickInterval: 24 * 3600 * 1000,
						/* tickInterval: 24 * 3600 * 1000, */
						lineColor: GRAY_COLOUR,
						labels: {
							formatter: function () { return formatDateForGraphAxis(new Date(this.value)) },
							rotation: 90,
							style: {
								color: 'black'
							}
						},
						gridLineWidth: 0
					},
					yAxis: {
						title: {
							text: this.$t('value')
						},
						labels: {
							style: {
								color: 'black'
							}
						},
						min: 0,
						max: this.maxValue,
						gridLineColor: GRAY_COLOUR,
						gridLineWidth: 0.25
					},
					credits: false,
					chart: {
						backgroundColor: BACKGROUND_COLOUR
					},
					series: [
						{
							type: 'scatter',
							marker: {
								radius: 5
							},
							color: BLUE_COLOUR,
							data: [],
							tooltip: {
								pointFormatter: function () { return `${formatDate(this.x, { dateStyle: 'medium' })}: ${this.y}${_this.measureUnit}` }
							},
							lineColor: GRAY_COLOUR
						},
						{
							type: 'scatter',
							color: RED_COLOUR,
							data: [],
							visible: false,
							tooltip: {
								pointFormatter: function () { return `${formatDate(this.x, { dateStyle: 'medium' })}: ${this.y}${_this.measureUnit}` }
							}
						}
					]
				}
		}
	}
}
</script>


// WEBPACK FOOTER //
// src/components/shared/ClinicHistoryVariableChart.vue